/**
 * Table utility functions for ProfessionalTable component
 * Includes cell value extraction, validation, UI helpers, and table type configuration
 */

import { Lead } from '../types';
import { isURL, isEmail } from './validation';
import { Briefcase, Users, FileText, Database, LucideIcon, CheckCircle, AlertCircle, Loader2, AlertTriangle } from 'lucide-react';

export interface LeadPromptSegment {
  id: string;
  segment_text: string;
  segment_order: number;
  created_at: string;
  updated_at: string;
  lead_table_id: string;
}

// Re-export validation functions for convenience
export { isURL, isEmail };

/**
 * Generate a consistent hash key for criterion_text.
 * Uses DJB2 hash algorithm - simple, fast, deterministic, and sufficient for our use case.
 *
 * This addresses the concern about long criterion_text values being used as dictionary keys
 * by creating compact 16-character hex strings that maintain uniqueness while reducing memory usage.
 *
 * @param text - The criterion text to hash
 * @returns A 16-character hash string (e.g., "00000001a3f8e2b9")
 */
export const hashCriterionTextSync = (text: string): string => {
  if (!text) return '';

  // DJB2 hash algorithm - simple, fast, and deterministic
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash) + text.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to hex and pad to 16 characters for consistent length
  return Math.abs(hash).toString(16).padStart(16, '0').substring(0, 16);
};

/**
 * Table type definitions and configuration
 */
export type TableType = 'companies' | 'people' | 'custom';

export interface TableTypeConfig {
  icon: LucideIcon;
  label: string;
  subtitle?: string;
}

/**
 * All available table types as a constant array
 */
export const TABLE_TYPES: readonly TableType[] = ['companies', 'people', 'custom'] as const;

/**
 * Get icon and label configuration for a table type
 * Used across DefaultScreen and DatasetSidebar
 */
export function getTableTypeConfig(type: TableType | string): TableTypeConfig {
  switch (type) {
    case 'companies':
      return {
        icon: Briefcase,
        label: 'Companies',
        subtitle: 'Find organizations'
      };
    case 'people':
      return {
        icon: Users,
        label: 'People',
        subtitle: 'Find decision-makers'
      };
    case 'custom':
      return {
        icon: FileText,
        label: 'Custom',
        subtitle: 'Custom search'
      };
    default:
      return {
        icon: Database,
        label: 'Dataset'
      };
  }
}

/**
 * Get just the icon for a table type (used in DatasetSidebar)
 */
export function getTableTypeIcon(type?: string): LucideIcon {
  return getTableTypeConfig(type || 'custom').icon;
}

/**
 * Status indicator configuration for datasets
 */
export interface StatusIndicator {
  icon: LucideIcon;
  color: string;
  spinning: boolean;
}

/**
 * Get status indicator configuration for dataset status
 * Used across AppSidebar, MobileNav, and DatasetSidebar
 */
export function getDatasetStatusIndicator(status?: string): StatusIndicator {
  switch (status) {
    case 'completed':
      return { icon: CheckCircle, color: '#10B981', spinning: false };
    case 'completed_no_results':
      return { icon: AlertCircle, color: '#F59E0B', spinning: false };
    case 'generating':
    case 'processing':
      return { icon: Loader2, color: '#6B7280', spinning: true };
    default:
      return { icon: AlertTriangle, color: '#EF4444', spinning: false };
  }
}

/**
 * Get display name for table type (singular or plural)
 * Used in LeadsTable and other components
 */
export function getTableTypeDisplayName(type: TableType | string, isPlural: boolean = true): string {
  switch (type) {
    case 'companies':
      return isPlural ? 'companies' : 'company';
    case 'people':
      return isPlural ? 'people' : 'person';
    case 'custom':
      return isPlural ? 'results' : 'result';
    default:
      return isPlural ? 'results' : 'result';
  }
}

/**
 * Check if cell content is truncated (overflowing)
 */
export const isCellTruncated = (element: HTMLElement): boolean => {
  return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
};

/**
 * Check if content is a Match badge (to skip hover preview)
 */
export const isMatchBadge = (content: string): boolean => {
  if (!content || typeof content !== 'string') return false;
  const trimmed = content.trim().toLowerCase();
  return trimmed === 'match';
};

/**
 * Calculate popup position within viewport bounds
 * Ensures popup is always visible and doesn't overflow the viewport
 */
export const getPopupPosition = (
  rect: DOMRect,
  popupWidth: number,
  popupHeight: number
): { x: number; y: number } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  // Calculate the visible viewport bounds
  const viewportTop = scrollY;
  const viewportBottom = scrollY + viewportHeight;
  const viewportLeft = scrollX;
  const viewportRight = scrollX + viewportWidth;

  let x = rect.left;
  let y = rect.bottom + 5;

  // Check if popup would go beyond right edge of viewport
  if (x + popupWidth > viewportRight) {
    x = Math.max(viewportLeft + 10, rect.right - popupWidth);
    // If still doesn't fit, align to right edge with margin
    if (x + popupWidth > viewportRight) {
      x = viewportRight - popupWidth - 10;
    }
  }

  // Check if popup would go beyond left edge of viewport
  if (x < viewportLeft + 10) {
    x = viewportLeft + 10;
  }

  // Check if popup would go beyond bottom edge of viewport
  if (y + popupHeight > viewportBottom - 10) {
    // Try positioning above the element
    const aboveY = rect.top - popupHeight - 5;
    if (aboveY >= viewportTop + 10) {
      y = aboveY;
    } else {
      // If doesn't fit above either, position at bottom of viewport with margin
      y = Math.max(viewportTop + 10, viewportBottom - popupHeight - 10);
    }
  }

  // Final check if popup would go beyond top edge of viewport
  if (y < viewportTop + 10) {
    y = viewportTop + 10;
  }

  return { x, y };
};

/**
 * Check if a column is a base column that shouldn't be modified
 */
export const isBaseColumn = (columnName: string, tableType: 'companies' | 'people' | 'custom'): boolean => {
  const LEAD_TYPE_BASE_COLUMNS = {
    companies: ['Name', 'Description', 'URL'],
    people: ['Name', 'Company', 'Job Title', 'URL'],
    custom: ['Name', 'Description', 'URL']
  };

  const baseColumns = LEAD_TYPE_BASE_COLUMNS[tableType] || LEAD_TYPE_BASE_COLUMNS.custom;
  return baseColumns.includes(columnName);
};

/**
 * Get the value for a specific cell in the table
 * Handles both regular columns and segment columns
 */
export const getCellValue = (lead: Lead, columnKey: string, columnName?: string): any => {
  // Handle segment columns
  if (columnKey.startsWith('segment_')) {
    const segmentId = columnKey.replace('segment_', '');
    return getSegmentValue(lead, segmentId);
  }

  // Handle 'name' column specifically
  if (columnKey === 'name' || columnName === 'Name') {
    return lead.name || lead.data?.name || lead.data?.Name || lead.data?.company_name || 'Unnamed';
  }

  // Try to find the value using both the column key and the original column name
  if (columnName && lead.data?.[columnName] !== undefined) {
    return lead.data[columnName];
  }

  // Fallback to the column key
  return lead.data?.[columnKey] || lead.data?.[columnKey.replace(/_\d+$/, '')] || '';
};


/**
 * Get the segment value (Match/Miss/Unclear/Unknown) for a lead
 */
export const getSegmentValue = (
  lead: Lead,
  segmentId: string
): string => {
  const reasoning = lead.reasoning;
  if (!reasoning) {
    return 'Unknown';
  }

  // Try direct lookup first (legacy format where key is the segmentId)
  let segmentReasoning = reasoning[segmentId];

  // If not found, search by hashing criterion_text (new format)
  if (!segmentReasoning) {
    const entries = Object.values(reasoning);
    segmentReasoning = entries.find((entry: any) =>
      entry?.criterion_text && hashCriterionTextSync(entry.criterion_text) === segmentId
    );
  }

  if (!segmentReasoning) {
    return 'Unknown';
  }

  const satisfied = segmentReasoning.satisfied;

  // Map V2 satisfied values to display text
  if (satisfied === 'yes') {
    return 'Match';
  } else if (satisfied === 'no') {
    return 'Miss';
  } else if (satisfied === 'unclear') {
    return 'Unclear';
  } else {
    return 'Unknown';
  }
};

/**
 * Get the confidence score for a segment
 * Returns a value between 0 and 1
 */
export const getSegmentConfidence = (
  lead: Lead,
  segmentId: string
): number => {
  const reasoning = lead.reasoning;
  if (!reasoning) {
    return 0;
  }

  const segmentReasoning = reasoning[segmentId];

  if (!segmentReasoning) {
    return 0;
  }

  const satisfied = segmentReasoning.satisfied;
  // Map V2 satisfied values to confidence scores for UI consistency
  if (satisfied === 'yes') {
    return 0.9; // High confidence
  } else if (satisfied === 'no') {
    return 0.1; // Low confidence
  } else if (satisfied === 'unclear') {
    return 0.5; // Medium confidence
  } else {
    return 0;
  }
};

/**
 * Get cell loading state from lead's cellStates
 * Provides consistent access pattern across codebase
 */
export const isCellLoading = (lead: Lead, columnName: string): boolean => {
  return lead.cellStates?.[columnName]?.isLoading || false;
};

/**
 * Get display URL from lead's metadata
 * Provides consistent access pattern across codebase
 */
export const getDisplayUrl = (lead: Lead): string | null => {
  return lead.metadata?.displayUrl || null;
};
